/**
 * Organization API Client
 * Handles communication with organization, advertiser, member, and credit endpoints
 */

import { authService } from './authService';
import { withOrgHeader } from './apiContext';

// Base URL with default fallback
const API_BASE_URL = import.meta.env.VITE_VIDEO_API_BASE_URL || 'http://localhost:5001';

/**
 * Custom error class for Organization API errors
 */
class OrganizationApiError extends Error {
  constructor(message, statusCode = 0, details = null) {
    super(message);
    this.name = 'OrganizationApiError';
    this.statusCode = statusCode;
    this.details = details;
  }

  get isNetworkError() {
    return this.statusCode === 0;
  }

  get isClientError() {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  get isServerError() {
    return this.statusCode >= 500;
  }

  get isPermissionDenied() {
    return this.statusCode === 403;
  }

  get isNotFound() {
    return this.statusCode === 404;
  }
}

/**
 * Organization API Client Class
 */
class OrganizationApiClient {
  constructor(baseUrl = null) {
    this.baseUrl = baseUrl || API_BASE_URL;
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Centralized request handler with authentication.
   *
   * @param {string} path
   * @param {Object} [options] - Standard fetch options.
   * @param {Object} [requestOptions]
   * @param {boolean} [requestOptions.includeOrg=true] - Attach the active
   *   organization header. Set false for endpoints that intentionally operate
   *   outside an org scope (e.g. listing the user's organizations).
   */
  async _makeRequest(path, options = {}, { includeOrg = true } = {}) {
    const url = `${this.baseUrl}${path}`;
    const headers = withOrgHeader(
      { ...this.headers, ...(options.headers || {}) },
      { includeOrg },
    );

    try {
      const response = await fetch(url, {
        credentials: 'include', // Required for session cookies
        ...options,
        headers,
      });

      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        console.warn('Unauthorized - redirecting to login');
        authService.handleUnauthorized();
        throw new OrganizationApiError('Unauthorized', 401, { redirect: true });
      }

      // Handle 403 Forbidden - permission denied
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        throw new OrganizationApiError(
          errorData.detail || 'Permission denied',
          403,
          errorData
        );
      }

      if (!response.ok) {
        if (response.status === 404) {
          throw new OrganizationApiError('Resource not found', 404);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new OrganizationApiError(
          errorData.detail || errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData
        );
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      if (error instanceof OrganizationApiError) {
        throw error;
      }
      throw new OrganizationApiError(`Network error: ${error.message}`, 0, error);
    }
  }

  // ============================================
  // ORGANIZATIONS
  // ============================================

  /**
   * List all organizations the current user belongs to
   * GET /api/v1/organizations
   * @returns {Promise<Array>} Array of organizations with user's role in each
   */
  async listOrganizations() {
    const data = await this._makeRequest('/api/v1/organizations');
    return data.organizations || data || [];
  }

  /**
   * Get organization details
   * GET /api/v1/organizations/:id
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Organization details
   */
  async getOrganization(orgId) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}`);
  }

  /**
   * Create a new organization
   * POST /api/v1/organizations
   * @param {Object} data - Organization data
   * @param {string} data.name - Organization name
   * @param {string} [data.slug] - URL-safe slug (auto-generated if not provided)
   * @returns {Promise<Object>} Created organization
   */
  async createOrganization(data) {
    if (!data || !data.name) {
      throw new Error('Organization name is required');
    }
    return this._makeRequest('/api/v1/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update organization details
   * PATCH /api/v1/organizations/:id
   * @param {string} orgId - Organization ID
   * @param {Object} data - Fields to update
   * @param {string} [data.name] - Organization name
   * @param {string} [data.slug] - URL-safe slug
   * @param {Object} [data.settings] - Organization settings
   * @returns {Promise<Object>} Updated organization
   */
  async updateOrganization(orgId, data) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete organization (soft delete)
   * DELETE /api/v1/organizations/:id
   * @param {string} orgId - Organization ID
   * @returns {Promise<void>}
   */
  async deleteOrganization(orgId) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // AGENCIES
  // ============================================

  /**
   * List agencies in an organization
   * GET /api/v1/organizations/:orgId/agencies
   * @param {string} orgId - Organization ID
   * @returns {Promise<Array>} Array of agencies
   */
  async listAgencies(orgId) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    const data = await this._makeRequest(`/api/v1/organizations/${orgId}/agencies`);
    return data.agencies || data || [];
  }

  /**
   * Create a new agency
   * POST /api/v1/organizations/:orgId/agencies
   * @param {string} orgId - Organization ID
   * @param {Object} data - Agency data
   * @param {string} data.name - Agency name
   * @returns {Promise<Object>} Created agency
   */
  async createAgency(orgId, data) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    if (!data || !data.name) {
      throw new Error('Agency name is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}/agencies`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get agency details
   * GET /api/v1/agencies/:id
   * @param {string} agencyId - Agency ID
   * @returns {Promise<Object>} Agency details
   */
  async getAgency(agencyId) {
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }
    return this._makeRequest(`/api/v1/agencies/${agencyId}`);
  }

  /**
   * Update agency
   * PATCH /api/v1/agencies/:id
   * @param {string} agencyId - Agency ID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>} Updated agency
   */
  async updateAgency(agencyId, data) {
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }
    return this._makeRequest(`/api/v1/agencies/${agencyId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete agency (soft delete)
   * DELETE /api/v1/agencies/:id
   * @param {string} agencyId - Agency ID
   * @returns {Promise<void>}
   */
  async deleteAgency(agencyId) {
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }
    return this._makeRequest(`/api/v1/agencies/${agencyId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // ADVERTISERS
  // ============================================

  /**
   * List advertisers in an organization
   * GET /api/v1/organizations/:orgId/advertisers
   * @param {string} orgId - Organization ID
   * @param {Object} [options] - Query options
   * @param {string} [options.agencyId] - Filter by agency
   * @param {number} [options.page] - Page number
   * @param {number} [options.pageSize] - Items per page
   * @returns {Promise<Object>} Paginated advertisers
   */
  async listAdvertisers(orgId, options = {}) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }

    const params = new URLSearchParams();
    if (options.agencyId) params.append('agency_id', options.agencyId);
    if (options.page) params.append('page', options.page.toString());
    if (options.pageSize) params.append('page_size', options.pageSize.toString());

    const query = params.toString();
    const path = `/api/v1/organizations/${orgId}/advertisers${query ? `?${query}` : ''}`;
    const data = await this._makeRequest(path);

    return {
      advertisers: data.advertisers || data || [],
      total: data.total || 0,
      page: data.page || 1,
      pageSize: data.page_size || 20,
    };
  }

  /**
   * Get advertiser details
   * GET /api/v1/advertisers/:id
   * @param {string} advertiserId - Advertiser ID
   * @returns {Promise<Object>} Advertiser details
   */
  async getAdvertiser(advertiserId) {
    if (!advertiserId) {
      throw new Error('Advertiser ID is required');
    }
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}`);
  }

  /**
   * Create a new advertiser
   * POST /api/v1/organizations/:orgId/advertisers
   * @param {string} orgId - Organization ID
   * @param {Object} data - Advertiser data
   * @param {string} data.name - Advertiser name
   * @param {string} [data.agency_id] - Optional agency ID
   * @returns {Promise<Object>} Created advertiser
   */
  async createAdvertiser(orgId, data) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    if (!data || !data.name) {
      throw new Error('Advertiser name is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}/advertisers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update advertiser
   * PATCH /api/v1/advertisers/:id
   * @param {string} advertiserId - Advertiser ID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>} Updated advertiser
   */
  async updateAdvertiser(advertiserId, data) {
    if (!advertiserId) {
      throw new Error('Advertiser ID is required');
    }
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete advertiser (soft delete)
   * DELETE /api/v1/advertisers/:id
   * @param {string} advertiserId - Advertiser ID
   * @returns {Promise<void>}
   */
  async deleteAdvertiser(advertiserId) {
    if (!advertiserId) {
      throw new Error('Advertiser ID is required');
    }
    return this._makeRequest(`/api/v1/advertisers/${advertiserId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // MEMBERS
  // ============================================

  /**
   * List members of an organization
   * GET /api/v1/organizations/:orgId/members
   * @param {string} orgId - Organization ID
   * @returns {Promise<Array>} Array of members with roles
   */
  async listMembers(orgId) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    const data = await this._makeRequest(`/api/v1/organizations/${orgId}/members`);
    return data.members || data || [];
  }

  /**
   * Add a member to an organization
   * POST /api/v1/organizations/:orgId/members
   * @param {string} orgId - Organization ID
   * @param {Object} data - Member data
   * @param {string} data.email - User email to add
   * @param {string} data.role - Role to assign ('admin', 'editor', 'viewer')
   * @returns {Promise<Object>} Created membership with user info
   */
  async addMember(orgId, data) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    if (!data || !data.email) {
      throw new Error('User email is required');
    }
    if (!data.role) {
      throw new Error('Role is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a member's role
   * PATCH /api/v1/organizations/:orgId/members/:userId
   * @param {string} orgId - Organization ID
   * @param {string} userId - User ID to update
   * @param {string} role - New role ('owner', 'admin', 'editor', 'viewer')
   * @returns {Promise<Object>} Updated membership
   */
  async updateMemberRole(orgId, userId, role) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (!role) {
      throw new Error('Role is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  /**
   * Remove a member from an organization
   * DELETE /api/v1/organizations/:orgId/members/:userId
   * @param {string} orgId - Organization ID
   * @param {string} userId - User ID to remove
   * @returns {Promise<void>}
   */
  async removeMember(orgId, userId) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    if (!userId) {
      throw new Error('User ID is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // CREDITS
  // ============================================

  /**
   * Get organization credits balance and transaction history
   * GET /api/v1/organizations/:orgId/credits
   * @param {string} orgId - Organization ID
   * @param {Object} [options] - Query options
   * @param {number} [options.limit] - Number of transactions to return
   * @param {number} [options.offset] - Transaction offset for pagination
   * @returns {Promise<Object>} Balance and transactions
   */
  async getCredits(orgId, options = {}) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }

    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const query = params.toString();
    const path = `/api/v1/organizations/${orgId}/credits${query ? `?${query}` : ''}`;
    const data = await this._makeRequest(path);

    return {
      balance: data.balance || 0,
      transactions: data.transactions || [],
      pagination: data.pagination || { total: 0, limit: 50, offset: 0 },
    };
  }

  /**
   * Purchase credits for an organization (owner only)
   * POST /api/v1/organizations/:orgId/credits/purchase
   * @param {string} orgId - Organization ID
   * @param {Object} data - Purchase data
   * @param {number} data.amount - Number of credits to purchase
   * @param {string} [data.payment_method_id] - Payment method ID
   * @returns {Promise<Object>} Purchase confirmation with new balance
   */
  async purchaseCredits(orgId, data) {
    if (!orgId) {
      throw new Error('Organization ID is required');
    }
    if (!data || !data.amount) {
      throw new Error('Credit amount is required');
    }
    return this._makeRequest(`/api/v1/organizations/${orgId}/credits/purchase`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // PERMISSIONS
  // ============================================

  /**
   * Check user's permission for a specific resource
   * GET /api/v1/permissions/check
   * @param {string} resourceType - Resource type ('organization', 'agency', 'advertiser', 'project')
   * @param {string} resourceId - Resource ID
   * @returns {Promise<Object>} Permission result
   */
  async checkPermission(resourceType, resourceId) {
    if (!resourceType) {
      throw new Error('Resource type is required');
    }
    if (!resourceId) {
      throw new Error('Resource ID is required');
    }

    const params = new URLSearchParams();
    params.append('resource_type', resourceType);
    params.append('resource_id', resourceId);

    const data = await this._makeRequest(`/api/v1/permissions/check?${params.toString()}`);

    return {
      allowed: data.allowed || false,
      role: data.role || null,
      level: data.level || 0,
      source: data.source || 'none',
    };
  }
}

/**
 * Role constants
 */
export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  BRIEF_EDITOR: 'brief_editor',
  VIEWER: 'viewer',
};

/**
 * Role levels for permission comparison
 */
export const ROLE_LEVELS = {
  [ROLES.OWNER]: 5,
  [ROLES.ADMIN]: 4,
  [ROLES.EDITOR]: 3,
  [ROLES.BRIEF_EDITOR]: 2,
  [ROLES.VIEWER]: 1,
};

/**
 * Transaction types for credits
 */
export const TRANSACTION_TYPES = {
  PURCHASE: 'purchase',
  CONSUMPTION: 'consumption',
  REFUND: 'refund',
  ADJUSTMENT: 'adjustment',
};

// Export singleton instance
export const organizationApiClient = new OrganizationApiClient();
export { OrganizationApiClient, OrganizationApiError };
